

import { useEffect, useReducer } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UnexpectedTypeError } from 'errors';
import Identifiable from 'interfaces/Identifiable';
import { ErrorCause } from 'models/ErrorResponse';

import DataSetReducerMessage from '../interfaces/DataSetReducerMessage';
import SingleEntity from '../interfaces/SingleEntity';
import { ApiService } from '../services/ApiService';

export const useSingleEntity = <T extends Identifiable>(
  apiBaseUrl: string,
  apiEndpoint: string,
  entityId: number | undefined,
) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const scheduleService = new ApiService<T>(apiBaseUrl, apiEndpoint);

  const defaultApiResponse: SingleEntity<T> = {
    entity: {} as T,
    entityId,
    isLoading: true,
    isError: false,
    error: undefined,
  };

  const apiResponseReducer = (
    _priorApiResponse: SingleEntity<T>,
    fieldUpdate: DataSetReducerMessage,
  ): SingleEntity<T> => {
    const updatedApiResponse: SingleEntity<T> = { ..._priorApiResponse };
    (updatedApiResponse as any)[fieldUpdate.type] = fieldUpdate.value;

    switch (fieldUpdate.type) {
      case 'entity':
        updatedApiResponse.isLoading = false;
        updatedApiResponse.isError = false;
        break;
      case 'error':
        updatedApiResponse.isError = true;
        updatedApiResponse.isLoading = false;
        break;
      case 'entityId':
        updatedApiResponse.entity = {} as T;
        updatedApiResponse.isLoading = true;
        updatedApiResponse.isError = false;
        break;
      default:
        throw new UnexpectedTypeError();
    }

    return updatedApiResponse;
  };

  const [entityRequest, setEntityRequest] = useReducer(apiResponseReducer, defaultApiResponse);

  useEffect(() => {
    if (!entityId) {
      setEntityRequest({ type: 'entity', value: {} });
      return;
    }

    const fetchData = async () => {
      const token = await getAccessTokenSilently();
      if (!token) {
        setEntityRequest({ type: 'error', value: new Error('401 unauthorized', { cause: ErrorCause.AuthenticationError }) });
      } else {
        scheduleService.setAccessToken(token);
        const data = await scheduleService.find(entityId!);
        setEntityRequest({ type: 'entity', value: data });
      }
    };

    fetchData().catch((e) => {
      setEntityRequest({ type: 'error', value: e });
    });
  }, [entityId, isAuthenticated]);

  return { entityRequest, setEntityRequest };
};
